import * as React from "react"

import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { GripVertical, SlidersHorizontal } from "lucide-react"

import { cn } from "../../cn"
import { Button } from "../Button/Button"
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverItem,
  PopoverTitle,
  PopoverTrigger,
} from "../Popover/Popover"
import { SearchInput } from "../SearchInput/SearchInput"
import { Tooltip } from "../Tooltip/Tooltip"
import { columnLabel } from "./TableInternals"
import type { ColumnDef } from "./types"

export interface TableColumnToggleProps<T> {
  /** The same `columns` passed to the Table — provides ids and header labels. */
  columns: ColumnDef<T>[]
  /** Render the shared selector directly inside an existing Display menu. */
  inline?: boolean
  /** Current visibility map (an absent / `true` entry means visible). */
  columnVisibility: Record<string, boolean>
  /** Receives the next visibility map. Lift this into the Table's matching prop. */
  onColumnVisibilityChange: (visibility: Record<string, boolean>) => void
  /**
   * Current column order (ids), mirroring the Table's `columnOrder` prop.
   * Pair with `onColumnOrderChange` to enable drag-to-reorder in the menu; omit
   * both for a visibility-only menu. Lift the state up and pass it to both this
   * and the `Table`.
   */
  columnOrder?: string[]
  /**
   * Receives the next order. Required alongside `columnOrder` to enable
   * reordering; a column opts out with `enableReordering: false`.
   */
  onColumnOrderChange?: (order: string[]) => void
  /**
   * When provided, renders a "Reset to default" item that calls this. The
   * consumer owns what "default" means — typically clearing both the order and
   * visibility overrides.
   */
  onReset?: () => void
  /**
   * Fewest columns that must stay visible; the last ones cannot be hidden.
   * Pass 0 when an empty selection is a valid state. @default 1
   */
  minVisible?: number
  /** Prevent a specific visibility transition, such as exceeding a column cap. */
  isColumnVisibilityDisabled?: (
    column: ColumnDef<T>,
    isVisible: boolean,
  ) => boolean
  /** Show a search field for filtering the column list. @default true */
  searchable?: boolean
  /** Search input placeholder and accessible label. */
  searchPlaceholder?: string
  /** Constrain or otherwise style the portaled popover content. */
  contentClassName?: string
  /** Show the full column label only when its chip is truncated. @default true */
  showFullLabelOnHover?: boolean
  /** Trigger label. @default "Visible columns" */
  label?: string
  /** Render the trigger as a compact icon button with the label in a tooltip. */
  iconOnly?: boolean
  /** Data attributes forwarded to the portaled popover content. */
  contentDataAttributes?: {
    [attributeName: `data-${string}`]: string | undefined
  }
}

/**
 * The pill a column chip wears. A shown column carries the info accent every
 * other selection in the product carries; a hidden one is the same pill left
 * empty, drawn in the neutral stroke.
 *
 * It sits on the element wrapping the chip's button so that a drag handle
 * sharing the pill is inside the one fill and the one stroke.
 *
 * Hovering a hidden column washes it in `--surface-selected`, the lighter step
 * of the same blue the shown chips are filled with, so the hover points at the
 * accent the click is about to bring without already claiming it. A chip whose
 * toggle is disabled keeps its resting skin, because the click it is pointing
 * at is a no-op.
 */
function columnChipSkin(visible: boolean): string {
  return cn(
    "flex min-w-0 max-w-40 items-center rounded-full border transition-colors duration-150",
    visible
      ? "border-transparent bg-coval-info-surface text-coval-info-on-surface"
      : cn(
          "border-coval-border bg-transparent text-coval-text-muted",
          // The wash is light enough for ordinary ink, so the label darkens to
          // the text token rather than reaching for the on-surface one, and
          // the stroke joins the fill so the pill hovers as one shape.
          "hover:border-surface-selected hover:bg-surface-selected hover:text-coval-text",
          // The disabled state lives on the button inside the pill, so the
          // pill reads it off its own subtree to hold the resting skin.
          "has-[:disabled]:hover:border-coval-border has-[:disabled]:hover:bg-transparent has-[:disabled]:hover:text-coval-text-muted",
        ),
  )
}

/**
 * Moves `activeId` to `overId`'s position. With `within`, the move permutes
 * only those ids among their own slots, so ids outside it stay put.
 */
export function reorderColumns(
  order: string[],
  activeId: string,
  overId: string,
  within?: readonly string[],
): string[] {
  const scope = within ? new Set(within) : null
  const subset = scope ? order.filter((id) => scope.has(id)) : order
  const from = subset.indexOf(activeId)
  const to = subset.indexOf(overId)
  if (from === -1 || to === -1 || from === to) {
    return order
  }
  const moved = arrayMove(subset, from, to)
  if (!scope) {
    return moved
  }
  let next = 0
  return order.map((id) => (scope.has(id) ? moved[next++] : id))
}
/** Shared column chips for standalone and inline Display menus. */
export function TableColumnToggle<T>({
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  columnOrder,
  onColumnOrderChange,
  onReset,
  minVisible = 1,
  isColumnVisibilityDisabled,
  searchable = true,
  searchPlaceholder = "Search columns…",
  contentClassName,
  showFullLabelOnHover = true,
  label = "Visible columns",
  iconOnly = false,
  contentDataAttributes,
  inline = false,
}: TableColumnToggleProps<T>) {
  const [searchQuery, setSearchQuery] = React.useState("")
  const [openTooltip, setOpenTooltip] = React.useState<{
    columnId: string
    kind: "label" | "reorder"
  } | null>(null)
  const tooltipsDismissed = React.useRef(false)
  const handleTooltipChange = (
    columnId: string,
    kind: "label" | "reorder",
    open: boolean,
  ) => {
    if (open && !tooltipsDismissed.current) {
      setOpenTooltip({ columnId, kind })
    } else if (!open) {
      setOpenTooltip((current) =>
        current?.columnId === columnId && current.kind === kind
          ? null
          : current,
      )
    }
  }
  const toggleable = columns.filter((column) => column.enableHiding !== false)
  const rank = new Map(columnOrder?.map((id, index) => [id, index]))
  const orderedColumns = columnOrder
    ? [...toggleable].sort(
        (a, b) =>
          (rank.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
          (rank.get(b.id) ?? Number.MAX_SAFE_INTEGER),
      )
    : toggleable
  const filteredColumns = orderedColumns.filter((column) =>
    columnLabel(column)
      .toLowerCase()
      .includes(searchQuery.trim().toLowerCase()),
  )
  const isVisible = (id: string) => columnVisibility[id] ?? true
  const visibleCount = toggleable.filter((column) =>
    isVisible(column.id),
  ).length
  const reorderable = Boolean(columnOrder && onColumnOrderChange)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )
  const moveColumn = (
    activeId: string,
    overId: string,
    within: readonly string[],
  ) => {
    if (columnOrder && onColumnOrderChange) {
      onColumnOrderChange(reorderColumns(columnOrder, activeId, overId, within))
    }
  }
  // The chips a column can trade places with: the reorderable ones on screen,
  // in order, so a drag or Move left / Move right steps over a fixed column.
  const reorderableIds = filteredColumns
    .filter((column) => column.enableReordering !== false)
    .map((column) => column.id)
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over) {
      moveColumn(String(active.id), String(over.id), reorderableIds)
    }
  }
  // dnd-kit announces ids by default; a screen reader user knows the columns
  // by their header labels.
  const labelOf = (id: string | number) => {
    const column = columns.find((candidate) => candidate.id === String(id))
    return column ? columnLabel(column) : String(id)
  }
  const positionOf = (id: string | number) =>
    `position ${filteredColumns.findIndex((column) => column.id === String(id)) + 1} of ${filteredColumns.length}`
  const announcements: Announcements = {
    onDragStart: ({ active }) =>
      `Picked up ${labelOf(active.id)} column, ${positionOf(active.id)}.`,
    onDragOver: ({ active, over }) =>
      over
        ? `${labelOf(active.id)} column moved to ${positionOf(over.id)}.`
        : `${labelOf(active.id)} column is no longer over a position.`,
    onDragEnd: ({ active, over }) =>
      over
        ? `${labelOf(active.id)} column dropped at ${positionOf(over.id)}.`
        : `${labelOf(active.id)} column dropped.`,
    onDragCancel: ({ active }) =>
      `Moving ${labelOf(active.id)} column cancelled.`,
  }
  const chips = filteredColumns.map((column) => {
    const visible = isVisible(column.id)
    const draggable = reorderable && column.enableReordering !== false
    const disabled =
      (visible && visibleCount <= minVisible) ||
      Boolean(isColumnVisibilityDisabled?.(column, visible))
    const content = (
      <ColumnVisibilityChip
        label={columnLabel(column)}
        visible={visible}
        disabled={disabled}
        afterGrip={draggable}
        showFullLabelOnHover={showFullLabelOnHover}
        tooltipOpen={
          openTooltip?.columnId === column.id && openTooltip.kind === "label"
        }
        onTooltipOpenChange={(open) =>
          handleTooltipChange(column.id, "label", open)
        }
        contentDataAttributes={contentDataAttributes}
        onToggle={() =>
          onColumnVisibilityChange({
            ...columnVisibility,
            [column.id]: !visible,
          })
        }
      />
    )
    return reorderable ? (
      <SortableColumnChip
        key={column.id}
        id={column.id}
        label={columnLabel(column)}
        visible={visible}
        draggable={draggable}
        previousId={reorderableIds[reorderableIds.indexOf(column.id) - 1]}
        nextId={reorderableIds[reorderableIds.indexOf(column.id) + 1]}
        // Permute only the reorderable chips, so a fixed column between two of
        // them keeps its slot.
        onMove={(overId) => moveColumn(column.id, overId, reorderableIds)}
        tooltipOpen={
          openTooltip?.columnId === column.id && openTooltip.kind === "reorder"
        }
        onTooltipOpenChange={(open) =>
          handleTooltipChange(column.id, "reorder", open)
        }
        contentDataAttributes={contentDataAttributes}
      >
        {content}
      </SortableColumnChip>
    ) : (
      <span key={column.id} className={columnChipSkin(visible)}>
        {content}
      </span>
    )
  })
  const controls = (
    <div
      {...contentDataAttributes}
      className="space-y-2"
      onScrollCapture={() => {
        tooltipsDismissed.current = true
        setOpenTooltip(null)
      }}
      onPointerMoveCapture={() => {
        tooltipsDismissed.current = false
      }}
      onFocusCapture={() => {
        tooltipsDismissed.current = false
      }}
    >
      <div className="text-xs text-coval-text-muted">Visible columns</div>
      {searchable && (
        <SearchInput
          aria-label={searchPlaceholder}
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          onClear={() => setSearchQuery("")}
        />
      )}
      <div
        role="group"
        aria-label="Visible columns"
        // Scrolls, so it clips: it reaches 2px past its content on every side
        // and pads back in, so the chips keep their place and the focus ring
        // drawn 2px outside a chip stays inside the clip.
        className="-m-[2px] max-h-60 overflow-y-auto p-[2px]"
      >
        {reorderable ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            accessibility={{ announcements }}
          >
            <SortableContext
              items={filteredColumns.map((column) => column.id)}
              strategy={rectSortingStrategy}
            >
              <div className="flex flex-wrap gap-1">{chips}</div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="flex flex-wrap gap-1">{chips}</div>
        )}
        {!filteredColumns.length && (
          <p className="py-2 text-sm text-coval-text-muted">No columns found</p>
        )}
      </div>
      {onReset && (
        <Button type="button" variant="ghost" onClick={onReset}>
          Reset to default
        </Button>
      )}
    </div>
  )
  if (inline) {
    return controls
  }
  return (
    <Popover
      onOpenChange={(open) => {
        if (!open) {
          setSearchQuery("")
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          icon={iconOnly}
          tooltip={label}
          tooltipProps={{ side: "bottom" }}
        >
          <SlidersHorizontal aria-hidden />
          {!iconOnly && label}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        {...contentDataAttributes}
        align="start"
        className={cn("w-80 max-w-[calc(100vw-2rem)] p-4", contentClassName)}
      >
        {controls}
      </PopoverContent>
    </Popover>
  )
}

function ColumnVisibilityChip({
  label,
  visible,
  disabled,
  afterGrip,
  onToggle,
  showFullLabelOnHover,
  tooltipOpen,
  onTooltipOpenChange,
  contentDataAttributes,
}: {
  label: string
  visible: boolean
  disabled: boolean
  /** The label follows a reorder grip, which supplies the gap on its left. */
  afterGrip: boolean
  onToggle: () => void
  showFullLabelOnHover: boolean
  tooltipOpen: boolean
  onTooltipOpenChange: (open: boolean) => void
  contentDataAttributes?: TableColumnToggleProps<unknown>["contentDataAttributes"]
}) {
  const labelRef = React.useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = React.useState(false)
  React.useLayoutEffect(() => {
    const node = labelRef.current
    if (!node) {
      return
    }
    const measure = () => {
      setIsTruncated(node.scrollWidth > node.clientWidth)
    }
    measure()
    if (typeof ResizeObserver === "undefined") {
      return
    }
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [label])

  return (
    <Tooltip
      content={label}
      className="data-[state=closed]:hidden data-[state=closed]:animate-none"
      open={tooltipOpen && isTruncated}
      onOpenChange={onTooltipOpenChange}
      openOnInteraction={showFullLabelOnHover && isTruncated}
      contentDataAttributes={contentDataAttributes}
    >
      <Button
        type="button"
        variant="ghost"
        aria-label={label}
        aria-pressed={visible}
        disabled={disabled}
        className={cn(
          "h-6! min-w-0 max-w-full rounded-full bg-transparent px-1.5 text-xs font-normal",
          afterGrip && "pl-1",
          "text-inherit hover:bg-transparent hover:text-inherit",
          "disabled:hover:text-inherit",
          visible && "disabled:opacity-100",
        )}
        onClick={onToggle}
      >
        <span ref={labelRef} className="min-w-0 truncate">
          {label}
        </span>
      </Button>
    </Tooltip>
  )
}

/**
 * A reorderable chip. Its grip drags the chip, takes dnd-kit's keyboard
 * sensor (Space, then the arrow keys), and on a tap opens Move left / Move
 * right, so a pointer user who cannot drag can still reorder (WCAG 2.5.7). The
 * grip is a 24 CSS px square for its target size (WCAG 2.5.8), sized in px
 * because the root font size makes `size-6` smaller; its negative block margin
 * keeps the pill at the label's height.
 */
function SortableColumnChip({
  id,
  label,
  draggable,
  visible,
  previousId,
  nextId,
  onMove,
  tooltipOpen,
  onTooltipOpenChange,
  children,
  contentDataAttributes,
}: {
  id: string
  label: string
  draggable: boolean
  visible: boolean
  /** The reorderable column Move left trades places with, if any. */
  previousId: string | undefined
  /** The reorderable column Move right trades places with, if any. */
  nextId: string | undefined
  onMove: (overId: string) => void
  tooltipOpen: boolean
  onTooltipOpenChange: (open: boolean) => void
  children: React.ReactNode
  contentDataAttributes?: TableColumnToggleProps<unknown>["contentDataAttributes"]
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !draggable })
  const [menuOpen, setMenuOpen] = React.useState(false)
  const gripRef = React.useRef<HTMLButtonElement>(null)
  const titleId = React.useId()
  return (
    <Popover open={menuOpen} onOpenChange={setMenuOpen}>
      <PopoverAnchor asChild>
        <div
          ref={setNodeRef}
          style={{ transform: CSS.Transform.toString(transform), transition }}
          className={cn(
            columnChipSkin(visible),
            isDragging && "z-10 opacity-80 shadow-sm",
          )}
        >
          {draggable && (
            <Tooltip
              content={`Reorder ${label}`}
              className="data-[state=closed]:hidden data-[state=closed]:animate-none"
              open={tooltipOpen && !menuOpen}
              onOpenChange={onTooltipOpenChange}
              contentDataAttributes={contentDataAttributes}
            >
              <button
                ref={gripRef}
                type="button"
                aria-label={`Reorder ${label}`}
                className="-my-[2px] flex size-[24px] shrink-0 cursor-grab touch-none items-center justify-center rounded-full text-inherit opacity-70"
                {...attributes}
                {...listeners}
                // A press that never travels far enough to start a drag lands
                // here as a click; dnd-kit swallows the click that ends a drag.
                onClick={() => setMenuOpen(true)}
              >
                <GripVertical className="size-3" />
              </button>
            </Tooltip>
          )}
          {children}
        </div>
      </PopoverAnchor>
      <PopoverContent
        {...contentDataAttributes}
        aria-labelledby={titleId}
        className="w-40 p-1"
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          gripRef.current?.focus()
        }}
      >
        <PopoverTitle id={titleId}>Move {label}</PopoverTitle>
        <PopoverItem
          disabled={!previousId}
          onClick={() => previousId && onMove(previousId)}
        >
          Move left
        </PopoverItem>
        <PopoverItem
          disabled={!nextId}
          onClick={() => nextId && onMove(nextId)}
        >
          Move right
        </PopoverItem>
      </PopoverContent>
    </Popover>
  )
}
